import React from 'react';
import { Textarea } from '@mantine/core';

export interface AfaTextareaProps {
  /** Valor actual del textarea */
  value?: string;
  
  /** Callback cuando cambia el valor */
  onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  
  /** Dato que viene de Editar */
  defaultValue?: string;
  
  /** Placeholder del textarea */
  placeholder?: string;
  
  /** Label del campo */
  label?: string;
  
  /** Descripción adicional */
  description?: string;
  
  /** Mensaje de error */
  error?: string;
  
  /** Si es requerido */
  required?: boolean;
  
  /** Si está deshabilitado */
  disabled?: boolean;
  
  /** Tamaño del textarea */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  
  /** Radio de bordes */
  radius?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  
  /** Número de filas */
  rows?: number;
  
  /** Número mínimo de filas */
  minRows?: number;
  
  /** Número máximo de filas */
  maxRows?: number;
  
  /** Si se redimensiona automáticamente */
  autosize?: boolean;
  
  /** Solo lectura */
  readOnly?: boolean;
  
  /** Máximo número de caracteres */
  maxLength?: number;
  
  /** Props adicionales para el Textarea */
  textareaProps?: any;
  
  /** Estilos personalizados */
  styles?: any;
  
  /** Clase CSS */
  className?: string;
  
  /** Nombre del textarea para formularios */
  name?: string;
  
  /** ID del textarea */
  id?: string;
}

/**
 * Wrapper del Textarea de Mantine con defaults de AFA y estilos del tema
 * 
 * Ejemplos de uso:
 * 
 * // Básico
 * <AfaTextarea
 *   placeholder="Escriu el teu comentari"
 *   label="Comentari"
 * />
 * 
 * // Con autosize
 * <AfaTextarea
 *   placeholder="Descripció..."
 *   label="Descripció"
 *   autosize
 *   minRows={3}
 *   maxRows={10}
 * />
 * 
 * // Controlado
 * const [value, setValue] = useState('');
 * <AfaTextarea
 *   value={value}
 *   onChange={(e) => setValue(e.target.value)}
 *   label="Camp controlat"
 * />
 */
export function AfaTextarea({
  value,
  onChange,
  defaultValue,
  placeholder = 'Escriu aquí...',
  label,
  description,
  error,
  required = false,
  disabled = false,
  size = 'sm',
  radius = 'md',
  rows = 4,
  minRows,
  maxRows,
  autosize = false,
  readOnly = false,
  maxLength,
  textareaProps,
  styles,
  className,
  name,
  id,
}: AfaTextareaProps) {

  return (
    <Textarea
      value={value}
      onChange={onChange}
      defaultValue={defaultValue} 
      placeholder={placeholder}
      label={label}
      description={description}
      error={error}
      required={required}
      disabled={disabled}
      size={size}
      radius={radius}
      rows={rows}
      minRows={minRows}
      maxRows={maxRows}
      autosize={autosize}
      readOnly={readOnly}
      maxLength={maxLength}
      name={name}
      id={id}
      styles={{
        input: {
          borderColor: error ? 'var(--mantine-color-red-6)' : 'var(--mantine-color-brand-3)',
          '&:focus': {
            borderColor: 'var(--mantine-color-brand-5)',
            boxShadow: `0 0 0 2px var(--mantine-color-brand-1)`,
          },
          '&:disabled': {
            backgroundColor: 'var(--mantine-color-gray-1)',
            color: 'var(--mantine-color-gray-6)',
          },
        },
        label: {
          color: 'var(--mantine-other-black)',
          fontWeight: 500,
        },
        description: {
          color: 'var(--mantine-color-neutral-6)',
        },
        error: {
          color: 'var(--mantine-color-red-6)',
        },
        ...styles,
      }}
      {...(className && { className })}
      {...textareaProps}
    />
  );
}